/**
 * CIPAA Edit Page
 *
 * Page for editing existing CIPAA elections.
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Alert,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { PageShell } from '../../components';
import { CipaaForm } from './components';
import { Cipaa, CipaaFormData, UpdateCipaaRequest, CipaaStatus } from '../../types/cipaa';
import { cipaaService } from '../../services/cipaaService';

const CipaaEditPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { enqueueSnackbar } = useSnackbar();

  const [election, setElection] = useState<Cipaa | null>(null);
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (id) {
      loadElection(parseInt(id, 10));
    }
  }, [id]);

  const loadElection = async (electionId: number) => {
    setLoading(true);
    setError(null);
    try {
      const data = await cipaaService.getById(electionId);

      // Check if election can be edited
      if (![CipaaStatus.DRAFT, CipaaStatus.SCHEDULED].includes(data.status)) {
        setError('Esta eleição não pode ser editada no status atual');
        enqueueSnackbar('Eleição não pode ser editada', { variant: 'warning' });
      }

      setElection(data);
    } catch (err: any) {
      setError(err.message ?? 'Erro ao carregar eleição');
      enqueueSnackbar('Erro ao carregar eleição', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (formData: CipaaFormData) => {
    if (!id) return;

    setIsSubmitting(true);
    setError(null);

    try {
      const requestData: UpdateCipaaRequest = {
        name: formData.name,
        accessKey: formData.accessKey ?? undefined,
        startDate: formData.startDate,
        startTime: formData.startTime,
        endDate: formData.endDate,
        endTime: formData.endTime,
        electionDate: formData.electionDate ?? undefined,
        registrationOpenDate: formData.registrationOpenDate ?? undefined,
        candidateAnnouncementDate: formData.candidateAnnouncementDate ?? undefined,
        resultsAnnouncementDate: formData.resultsAnnouncementDate ?? undefined,
        termStartDate: formData.termStartDate ?? undefined,
        termEndDate: formData.termEndDate ?? undefined,
        registrationLocation: formData.registrationLocation ?? undefined,
        responsible: formData.responsible ?? undefined,
        effectiveMembers: formData.effectiveMembers ?? 0,
        alternateMembers: formData.alternateMembers ?? 0,
        employeeCount: formData.employeeCount,
        riskLevel: formData.riskLevel,
      };

      await cipaaService.update(parseInt(id, 10), requestData);
      enqueueSnackbar('Eleição atualizada com sucesso', { variant: 'success' });
      navigate(`/cipaa/${id}`);
    } catch (err: any) {
      setError(err.message ?? 'Erro ao atualizar eleição');
      enqueueSnackbar('Erro ao atualizar eleição', { variant: 'error' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate(`/cipaa/${id}`);
  };

  const breadcrumbItems = [
    { label: 'Início' },
    { label: 'Segurança do Trabalho' },
    { label: 'Eleições CIPAA' },
    { label: election?.name || 'Carregando...' },
    { label: 'Editar' },
  ];

  if (loading) {
    return (
      <PageShell breadcrumbs={breadcrumbItems} title="Editar Eleição">
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      </PageShell>
    );
  }

  if (!election) {
    return (
      <PageShell breadcrumbs={breadcrumbItems} title="Editar Eleição">
        <Alert severity="error">Eleição não encontrada</Alert>
      </PageShell>
    );
  }

  const canEdit = [CipaaStatus.DRAFT, CipaaStatus.SCHEDULED].includes(election.status);

  return (
    <PageShell breadcrumbs={breadcrumbItems} title={`Editar Eleição: ${election.name}`}>
      {/* Form Card */}
      <Card>
        <CardHeader title="Dados da Eleição" />
        <CardContent>
          {!canEdit ? (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Esta eleição não pode ser editada pois seu status atual não permite alterações.
            </Alert>
          ) : (
            <CipaaForm
              initialData={election}
              onSubmit={handleSubmit}
              onCancel={handleCancel}
              isSubmitting={isSubmitting}
              error={error}
            />
          )}
        </CardContent>
      </Card>
    </PageShell>
  );
};

export default CipaaEditPage;
